import DialogModal from '@/components/DialogModal';
import ManageProjectModal from '@/components/ManageProjectModal';
import { NoProjects } from '@/components/ProjectStep';
import ProjectListItem from '@/components/ProjectStep/ProjectListItem';
import SortableProjectListItem from '@/components/ProjectStep/SortableProjectListItem';
import { useAnimatePlanPages } from '@/contexts/AnimatedPlanPages';
import { CronofySyncEventPayload } from '@/gql/cronofy/types';
import { keys } from '@/gql/global/keys';
import DashboardLayout from '@/layouts/Dashboard';
import { refetchQueriesForArchive } from '@/pages/CategoryManager';
import { RoutesList } from '@/routes/routesList';
import { cleanCategoryForMutation } from '@/services/categories';
import { useCategories, useUpdateCategoryMutation } from '@/services/categories/hooks';
import { useCronofySyncEventMany } from '@/services/cronofy/hooks';
import {
  useCreateProject,
  useDeleteProject,
  useProjects,
  useUpdateProject,
  useUpdateProjectsOrderBatch,
} from '@/services/project/hooks';
import { IconChevronDown, IconChevronRight, IconPlus } from '@/theme/icons';
import { ProjectType } from '@/types/project';
import { pageTransition } from '@/utils/pageAnimation';
import rem from '@/utils/rem';
import { refetchQueries } from '@/utils/tanStackQuery';
import { trackProjectDeleted } from '@/utils/tracking';
import { Box, Button, Collapse, Flex, Grid, Icon, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

type CreateProjectPayload = {
  name: string;
  result: string;
  purpose: string;
  categoryId: string;
};

function Project() {
  const { t } = useTranslation('Projects');
  const { t: tProjectDetail } = useTranslation('ProjectDetail');
  const navigate = useNavigate();

  const toast = useToast();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openArchiveModal, setOpenArchiveModal] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [projectInfo, setProjectInfo] = useState<ProjectType>();
  const [isDropdownOpen, setDropdownOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localProjects, setLocalProjects] = useState<ProjectType[] | null>(null);

  const { data: categories } = useCategories();

  const { data: projects, isLoading } = useProjects({
    refetchOnMount: true,
  });

  const { pageNavigationEffect } = useAnimatePlanPages();

  const {
    isOpen: isOpenCreateProjectModal,
    onOpen: openCreateProjectModal,
    onClose: onCloseCreateProjectModal,
  } = useDisclosure();

  const { mutateAsync: createProject, isLoading: isCreatingProject } = useCreateProject();
  const { mutateAsync: deleteProject } = useDeleteProject();
  const { mutateAsync: updateProject } = useUpdateProject();
  const { mutateAsync: updateProjectsOrder } = useUpdateProjectsOrderBatch();
  const { mutate: cronofySyncEventMany } = useCronofySyncEventMany();

  const updateCategory = useUpdateCategoryMutation();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const onDeleteProject = useCallback(async () => {
    if (!projectInfo?.id || !localProjects) {
      return;
    }

    const updatedLocalProjects = localProjects.filter((p) => p.id !== projectInfo.id);
    setLocalProjects(updatedLocalProjects);

    setOpenDeleteModal(false);
    onCloseCreateProjectModal();

    toast({
      title: t('toasts.projectDeleted'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    trackProjectDeleted();

    deleteProject(
      { id: projectInfo.id },
      {
        onError: () => {
          setLocalProjects(localProjects);
          toast({
            title: t('toasts.projectDeletedFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        },
      },
    );
  }, [deleteProject, projectInfo?.id, toast, t, onCloseCreateProjectModal, localProjects]);

  useEffect(() => {
    return () => onCloseCreateProjectModal();
  }, [onCloseCreateProjectModal]);

  useEffect(() => {
    projectInfo && isEdit && openCreateProjectModal();
  }, [projectInfo, isEdit, openCreateProjectModal]);

  const onClose = useCallback(() => {
    setProjectInfo(undefined);
    setIsEdit(false);
    onCloseCreateProjectModal();
  }, [onCloseCreateProjectModal]);

  useEffect(() => {
    if (projects?.project) {
      if (!localProjects) {
        setLocalProjects(projects.project);
      } else {
        const mergedProjects = projects.project.map((serverProject) => {
          const localProject = localProjects.find((local) => local.id === serverProject.id);
          if (localProject) {
            return {
              ...serverProject,
              order: localProject.order,
            };
          }
          return serverProject;
        });
        setLocalProjects(mergedProjects);
      }
    }
  }, [projects?.project, localProjects]);

  const [activeProjects, archiveProjects] = useMemo(() => {
    const projectsToUse = localProjects || projects?.project;
    if (!projectsToUse) return [[], []];

    const active = projectsToUse.filter((p) => !p.isArchived).sort((a, b) => (a.order || 0) - (b.order || 0));

    const archived = projectsToUse.filter((p) => p.isArchived).sort((a, b) => (a.order || 0) - (b.order || 0));

    return [active, archived];
  }, [localProjects, projects]);

  const archiveActiveProject = useCallback(async () => {
    if (!projectInfo || !localProjects) return;

    let newOrder: number;
    if (projectInfo.isArchived) {
      newOrder = Math.max(0, ...activeProjects.map((p) => p.order || 0)) + 1;
    } else {
      newOrder = Math.min(1, ...archiveProjects.map((p) => p.order || 0));
      if (newOrder > 1) newOrder = 1;
    }

    const updatedLocalProjects = localProjects.map((p) =>
      p.id === projectInfo.id ? { ...p, isArchived: !p.isArchived, order: newOrder } : p,
    );
    setLocalProjects(updatedLocalProjects);

    setOpenArchiveModal(false);
    setProjectInfo(undefined);

    toast({
      title: t(`toasts.project${projectInfo.isArchived ? 'Restored' : 'Archived'}`),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    updateProject(
      {
        id: projectInfo.id,
        name: projectInfo.name,
        result: projectInfo.result,
        purpose: projectInfo.purpose,
        categoryId: projectInfo.categoryId,
        imageUrl: projectInfo.imageUrl ?? null,
        isArchived: !projectInfo.isArchived,
        order: newOrder,
      },
      {
        onSuccess: async (data) => {
          const category = categories?.category.find((c) => c.id === projectInfo.categoryId);

          if (!category) return;

          if (category?.isArchived) {
            await updateCategory.mutateAsync({
              ...cleanCategoryForMutation(category),
              isArchived: !category.isArchived,
            });
          }
          if (data.updateProjectByPk.actions) {
            const eventsToSync: CronofySyncEventPayload[] = data.updateProjectByPk.actions
              .filter((action) => !action.isCanceled && action.scheduledDate && action.scheduledTime)
              .map((action) => ({
                eventId: action.id,
                eventType: 'Action',
                deleted: !projectInfo.isArchived,
              }));

            if (eventsToSync.length) {
              cronofySyncEventMany({ events: eventsToSync });
            }
          }

          refetchQueriesForArchive();
          refetchQueries([keys.project.category(category?.id).queryKey]);
        },
        onError: () => {
          const revertedLocalProjects = localProjects.map((p) =>
            p.id === projectInfo.id ? { ...p, isArchived: projectInfo.isArchived } : p,
          );
          setLocalProjects(revertedLocalProjects);
        },
      },
    );
  }, [
    projectInfo,
    updateProject,
    categories?.category,
    updateCategory,
    toast,
    t,
    activeProjects,
    archiveProjects,
    localProjects,
    cronofySyncEventMany,
  ]);

  const editProject = useCallback(
    async (payload: CreateProjectPayload) => {
      if (!projectInfo || !localProjects) return;

      const updatedLocalProjects = localProjects.map((p) => {
        if (p.id === projectInfo.id) {
          return {
            ...p,
            ...payload,
            order: p.order,
          };
        }
        return p;
      });
      setLocalProjects(updatedLocalProjects);

      updateProject(
        {
          ...payload,
          id: projectInfo.id,
          imageUrl: projectInfo.imageUrl ?? null,
          isArchived: projectInfo.isArchived,
          order: projectInfo.order,
        },
        {
          onSuccess: () => {
            onClose();
            toast({
              title: t('toasts.projectEdited'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [projectInfo, updateProject, onClose, toast, t, localProjects],
  );

  const onCreateProject = useCallback(
    async ({ name, purpose, result, categoryId }: CreateProjectPayload) => {
      const id = uuidv4();

      const category = categories?.category.find((c) => c.id === categoryId);

      const maxActiveOrder = Math.max(0, ...activeProjects.map((p) => p.order || 0));
      const initialOrder = maxActiveOrder + 1;

      const newProject: ProjectType = {
        id,
        name,
        categoryId,
        result,
        purpose,
        imageUrl: category?.selectedImageUrl ?? '',
        isArchived: false,
        order: initialOrder,
        actions: [],
      };

      if (localProjects) {
        setLocalProjects([...localProjects, newProject]);
      }

      try {
        await createProject({
          id,
          name,
          categoryId,
          result,
          purpose,
          imageUrl: category?.selectedImageUrl ?? '',
          isArchived: false,
          order: initialOrder,
        });
        navigate(`${RoutesList.ProjectsPage}/${id}`);
      } catch (error) {
        console.error('Failed to create project:', error);
        if (localProjects) {
          setLocalProjects(localProjects.filter((p) => p.id !== id));
        }
      }
    },
    [categories?.category, createProject, navigate, activeProjects, localProjects],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const activeProjectId = active.id as string;
      const overProjectId = over.id as string;

      const activeInActiveProjects = activeProjects.some((p) => p.id === activeProjectId);
      const overInActiveProjects = activeProjects.some((p) => p.id === overProjectId);

      if (activeInActiveProjects !== overInActiveProjects) {
        return;
      }

      const projectsToReorder = activeInActiveProjects ? activeProjects : archiveProjects;
      const oldIndex = projectsToReorder.findIndex((p) => p.id === activeProjectId);
      const newIndex = projectsToReorder.findIndex((p) => p.id === overProjectId);

      if (oldIndex === newIndex) {
        return;
      }

      const reorderedProjects = arrayMove(projectsToReorder, oldIndex, newIndex);
      const updatedReorderedProjects = reorderedProjects.map((project, index) => ({
        ...project,
        order: index + 1,
      }));

      const otherProjects = activeInActiveProjects ? archiveProjects : activeProjects;
      const newLocalProjects = [...updatedReorderedProjects, ...otherProjects];
      setLocalProjects(newLocalProjects);

      const updatedProjects = updatedReorderedProjects.map((project, index) => ({
        id: project.id,
        order: index + 1,
      }));

      try {
        await updateProjectsOrder(updatedProjects);
      } catch (error) {
        console.error('Failed to update project order:', error);
        toast({
          title: t('toasts.projectOrderUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [activeProjects, archiveProjects, updateProjectsOrder, toast, t],
  );

  if (isLoading || !projects) {
    return (
      <DashboardLayout pageTitle={t('pageTitle')} paddingX={rem(16)}>
        <Flex alignItems="center" justifyContent="center" marginTop={rem(128)}>
          <Spinner />
        </Flex>
      </DashboardLayout>
    );
  }

  const hasProjects = projects.project.length > 0;

  const rightSectionElements = (
    <Button
      height="full"
      marginLeft="auto"
      padding={rem(8)}
      leftIcon={<Icon as={IconPlus} color="text-primary" />}
      onClick={openCreateProjectModal}
      variant="secondary"
    >
      <Text textStyle="small" color="text-primary" fontWeight={900}>
        {t('buttons.createNewProject')}
      </Text>
    </Button>
  );

  return (
    <DashboardLayout
      pageTitle={t('pageTitle')}
      paddingX={hasProjects ? rem(16) : 0}
      rightSectionElements={rightSectionElements}
    >
      <motion.div
        className="row"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageNavigationEffect}
        transition={pageTransition}
      >
        {activeProjects.length > 0 || archiveProjects.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Projects Section */}
            <Box overflowY="auto" width="full" minHeight="42vh" borderRadius="md">
              <SortableContext items={activeProjects.map((p) => p.id)} strategy={rectSortingStrategy}>
                <Grid
                  alignItems={activeProjects.length === 0 ? 'center' : 'start'}
                  justifyContent={activeProjects.length === 0 ? 'center' : 'start'}
                  gridRowGap={rem(16)}
                  gridColumnGap={rem(16)}
                  gridTemplateColumns={activeProjects.length === 0 ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'}
                  width="full"
                  paddingBottom={rem(32)}
                >
                  {activeProjects.length > 0 ? (
                    activeProjects.map((project) => (
                      <SortableProjectListItem
                        key={project.id}
                        project={project}
                        isDragging={activeId === project.id}
                        setOpenDeleteModal={() => {
                          setProjectInfo(project);
                          setOpenDeleteModal(true);
                        }}
                        setOpenArchiveModal={() => {
                          setProjectInfo(project);
                          setOpenArchiveModal(true);
                        }}
                        setOpenEditModal={() => {
                          setProjectInfo(project);
                          setIsEdit(true);
                        }}
                        setOnClickExportButton={() => {
                          window.open(`${window.origin}/print/project/${project.id}`, '_blank');
                        }}
                      />
                    ))
                  ) : (
                    <Text color="gray.500" fontSize="lg" textAlign="center">
                      {t('emptyStates.noActiveProjects')}
                    </Text>
                  )}
                </Grid>
              </SortableContext>
            </Box>

            {/* Archive Projects Button */}
            <Flex
              alignItems="center"
              gap={2}
              width="full"
              marginBottom={rem(20)}
              background="background-tertiary"
              borderRadius="md"
              cursor="pointer"
              onClick={() => setDropdownOpen(!isDropdownOpen)}
              paddingX={4}
              paddingY={3}
            >
              <Text color="text-primary" fontWeight="800">
                {t('sections.archivedProjects')}
              </Text>
              <Icon
                as={isDropdownOpen ? IconChevronDown : IconChevronRight}
                width={rem(14)}
                height={rem(14)}
                color="text-primary"
              />
            </Flex>

            {/* Collapse for Archive Projects */}
            <Collapse animateOpacity in={isDropdownOpen}>
              <SortableContext items={archiveProjects.map((p) => p.id)} strategy={rectSortingStrategy}>
                <Grid
                  alignItems={archiveProjects.length === 0 ? 'center' : 'start'}
                  justifyContent={archiveProjects.length === 0 ? 'center' : 'start'}
                  gridRowGap={rem(16)}
                  gridColumnGap={rem(16)}
                  gridTemplateColumns={archiveProjects.length === 0 ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'}
                  width="full"
                  marginBottom={6}
                >
                  {archiveProjects.length > 0 ? (
                    archiveProjects.map((project) => (
                      <SortableProjectListItem
                        key={project.id}
                        project={project}
                        isDragging={activeId === project.id}
                        setOpenDeleteModal={() => {
                          setProjectInfo(project);
                          setOpenDeleteModal(true);
                        }}
                        setOpenArchiveModal={() => {
                          setProjectInfo(project);
                          setOpenArchiveModal(true);
                        }}
                        setOpenEditModal={() => {
                          setProjectInfo(project);
                          setIsEdit(true);
                        }}
                        setOnClickExportButton={() => {
                          window.open(`${window.origin}/print/project/${project.id}`, '_blank');
                        }}
                      />
                    ))
                  ) : (
                    <Text color="gray.500" fontSize="lg" textAlign="center">
                      {t('emptyStates.noArchivedProjects')}
                    </Text>
                  )}
                </Grid>
              </SortableContext>
            </Collapse>

            {/* Drag Overlay */}
            <DragOverlay>
              {activeId ? (
                <Box opacity={0.8}>
                  <ProjectListItem
                    project={[...activeProjects, ...archiveProjects].find((p) => p.id === activeId)!}
                    setOpenDeleteModal={() => {}}
                    setOpenArchiveModal={() => {}}
                    setOpenEditModal={() => {}}
                    setOnClickExportButton={() => {}}
                  />
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          projects?.project?.length === 0 && <NoProjects onCreateProjectClick={openCreateProjectModal} />
        )}

        {/* Modals */}
        {isOpenCreateProjectModal && (
          <ManageProjectModal
            isOpen={isOpenCreateProjectModal}
            isLoading={isCreatingProject}
            onSubmit={isEdit ? editProject : onCreateProject}
            onClose={onClose}
            onDelete={onDeleteProject}
            project={projectInfo}
          />
        )}

        {openDeleteModal && (
          <DialogModal
            cancelText={t('modals.deleteProject.cancelText')}
            confirmText={t('modals.deleteProject.confirmText')}
            confirmVariant="danger"
            isOpen={openDeleteModal}
            message={t('modals.deleteProject.message')}
            onCancel={() => {
              setProjectInfo(undefined);
              setOpenDeleteModal(false);
            }}
            onConfirm={onDeleteProject}
            title={t('modals.deleteProject.title')}
          />
        )}

        {openArchiveModal && (
          <DialogModal
            cancelText={t('modals.archiveRestoreProject.cancelText')}
            confirmText={t(
              `modals.archiveRestoreProject.confirmTexts.${projectInfo?.isArchived ? 'restore' : 'archive'}`,
            )}
            confirmVariant="primary"
            isOpen={openArchiveModal}
            message={
              projectInfo?.isArchived
                ? categories?.category.find((c) => c.id === projectInfo.categoryId)?.isArchived
                  ? tProjectDetail('archiveMessages.restoreWithCategoryConfirmation')
                  : tProjectDetail('archiveMessages.restoreConfirmation')
                : tProjectDetail('archiveMessages.archiveConfirmation')
            }
            onCancel={() => {
              setProjectInfo(undefined);
              setOpenArchiveModal(false);
            }}
            onConfirm={archiveActiveProject}
            title={t(`modals.archiveRestoreProject.titles.${projectInfo?.isArchived ? 'restore' : 'archive'}`)}
          />
        )}
      </motion.div>
    </DashboardLayout>
  );
}

export default Project;
